import { inject, Injectable } from '@angular/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { PasswordRequirementsInfo } from '@barthauer/basys/core/authentication';
import { PasswordValidationInfo } from 'libs/abc/core/authentication/src/lib/models/password.model';

@Injectable({ providedIn: 'root' })
export class PasswordValidationService {
  private readonly infinity = 2147483647;
  private res: ResourceSet;
  private resourceSetService = inject(ResourceManagerService);
  public validatePassword(
    value: string,
    passwordRequirements: PasswordRequirementsInfo | undefined
  ): PasswordValidationInfo {
    const trimmedValue = value.trim();
    const passwordValidationInfo = new PasswordValidationInfo();

    if (passwordRequirements) {
      if (trimmedValue.length < passwordRequirements.minLength) {
        passwordValidationInfo.minLength = true;
      }

      if (trimmedValue.length > passwordRequirements.maxLength) {
        passwordValidationInfo.maxLength = true;
      }

      if (passwordRequirements.lower && !/[a-z]/.test(trimmedValue)) {
        passwordValidationInfo.lower = true;
      }

      if (passwordRequirements.upper && !/[A-Z]/.test(trimmedValue)) {
        passwordValidationInfo.upper = true;
      }

      if (passwordRequirements.digit && !/\d/.test(trimmedValue)) {
        passwordValidationInfo.digit = true;
      }

      if (passwordRequirements.symbol && !/[!@#$%^&*()_+\=[\]{};':"\\|,.<>/?\-äÄöÖüÜß]/.test(trimmedValue)) {
        passwordValidationInfo.symbol = true;
      }
    }

    return passwordValidationInfo;
  }

  public async createPasswordRequirementsWarnings(passwortRequirements: PasswordRequirementsInfo): Promise<string[]> {
    if (!this.res) {
      this.res = await this.resourceSetService.getResourceSet('abc-management-users');
    }

    const messages: string[] = [];

    if (passwortRequirements.minLength > 0 && passwortRequirements.maxLength < this.infinity) {
      messages.push(
        this.res.get(
          'UserSettings-LengthRange',
          passwortRequirements.minLength.toString(),
          passwortRequirements.maxLength.toString()
        )
      );
    } else if (passwortRequirements.minLength > 0) {
      messages.push(this.res.get('UserSettings-MinLength', passwortRequirements.minLength.toString()));
    } else if (passwortRequirements.maxLength < this.infinity) {
      messages.push(this.res.get('UserSettings-MaxLength', passwortRequirements.maxLength.toString()));
    }

    if (passwortRequirements.lower) {
      messages.push(this.res.get('UserSettings-Lowercase'));
    }

    if (passwortRequirements.upper) {
      messages.push(this.res.get('UserSettings-Uppercase'));
    }

    if (passwortRequirements.digit) {
      messages.push(this.res.get('UserSettings-Number'));
    }

    if (passwortRequirements.symbol) {
      messages.push(this.res.get('UserSettings-SpecialCharacter'));
    }

    return messages;
  }
}
